// SPDX-FileCopyrightText: 2026 Mads Ringblom
//
// SPDX-License-Identifier: GPL-3.0-or-later

/**
 * The evidence tile (PLAN.md "Evidence tile"; v8 `evTile`): opened over the
 * building from a part (`?part=&ev=`), in the centre slot of Ressourcer and
 * Gennemgang. Three views of the same part:
 *
 *  - Foto — the part's resource image (`sys:view`, drawn cropped; "Intet
 *    billede valgt" when none — no best-frame fallback), then the frames
 *    that see it (`/instances/{cloud}/{id}/frames`), its centroid ringed;
 *    one opens large, "Brug som ressourcebillede" chooses it and "Del op fra
 *    frame N" goes on;
 *  - 360° — the nearest panorama, centred on it, "Brug som ressourcebillede"
 *    and "Åbn i Model";
 *  - Del op — the SAM3 flow on one frame → a new part
 *    (`POST /frames/{id}/segment/resource`).
 *
 * Reads the case from `useCaseStore()`, so the mode only passes the part code
 * and the view. Esc is the shell's (`escapePatch` closes the tile first); the
 * × does the same.
 */

import { useEffect, useId, useMemo, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';

import { api } from '../../../api/client';
import type { ResourceView, SegmentResourceResult, SurveyPart, SurveyType, VisibleFrame } from '../../../api/types';
import { useCaseStore } from '../../../app/bygning/CaseDataContext';
import { useCanEdit } from '../../../app/CaseRoleContext';
import { viewportPanoramaHref } from '../../../app/links';
import { useAsync } from '../../../app/useAsync';
import type { EvidenceKind } from '../../../bygning/selection';
import { resolveHighlightPart } from '../../../kortlaegning/evidence';
import { PANO_TEXT } from '../../../kortlaegning/pano';
import { chooseViewWrite } from '../../../kortlaegning/copy';
import { type FrameLookup, hasInstanceLink, instanceKey, photoStripModel } from '../../../kortlaegning/photo';
import { resourceIndex } from '../../../kortlaegning/resources';
import { selectionView } from '../../../kortlaegning/view';
import { IconButton, XIcon } from '../../IconButton';
import { SegmentedControl } from '../../SegmentedControl';
import { Spinner } from '../../Spinner';
import { FotoGrid } from './FotoGrid';
import { PanoView } from './PanoView';
import { SegmentView, type SegmentTarget } from './SegmentView';
import styles from './EvidenceTile.module.css';

export type { EvidenceKind };

export interface EvidenceTileProps {
  /** The part in focus (`?part=`). */
  partCode: string;
  /** The view (`?ev=`). */
  kind: EvidenceKind;
  /** Close the tile (`setSel({ ev: null })`). */
  onClose(): void;
  /** Switch view (`setSel({ ev: kind })`). */
  onKind(kind: EvidenceKind): void;
  /** Focus the part's room (the header's room name); omitted = plain text. */
  onFocusRoom?(roomId: number): void;
}

const TABS: readonly { kind: EvidenceKind; label: string }[] = [
  { kind: 'foto', label: 'Foto' },
  { kind: 'pano', label: '360°' },
  { kind: 'seg', label: 'Del op' },
];

function findPart(types: readonly SurveyType[], code: string): { type: SurveyType; part: SurveyPart } | null {
  for (const type of types) {
    const part = type.parts.find((p) => p.code === code);
    if (part) return { type, part };
  }
  return null;
}

export function EvidenceTile({ partCode, kind, onClose, onKind, onFocusRoom }: EvidenceTileProps) {
  const { data, actions } = useCaseStore();
  const navigate = useNavigate();
  const baseId = useId();

  const found = useMemo(() => findPart(data.types, partCode), [data.types, partCode]);
  const type = found?.type ?? null;
  const part = found?.part ?? null;
  const highlight = resolveHighlightPart(type, part);
  const linked = hasInstanceLink(highlight);
  const currentKey = linked ? instanceKey(highlight.cloud, highlight.instance_id) : null;

  // One frames lookup for Foto and Del op, tagged with its key so a previous
  // part's frames are never shown as this one's (see kortlaegning/photo.ts).
  const lookup = useAsync<FrameLookup>(
    async (signal) => {
      if (!linked) return { key: '', frames: [], failed: false };
      const key = instanceKey(highlight.cloud, highlight.instance_id);
      try {
        return { key, frames: await api.instanceFrames(highlight.cloud, highlight.instance_id, signal), failed: false };
      } catch (cause) {
        if (signal.aborted) throw cause;
        return { key, frames: [], failed: true };
      }
    },
    [highlight?.cloud, highlight?.instance_id],
  );
  const current = currentKey !== null && lookup.data?.key === currentKey ? lookup.data : null;
  const frames: VisibleFrame[] | null = current && !current.failed && current.frames.length > 0 ? current.frames : null;
  const frameMessage = photoStripModel(currentKey, lookup.data).message;

  // The resource view record (sys:view): what the part shows, and — for an
  // editor — the write a "Brug som ressourcebillede" choice makes. A view is
  // only ever replaced, never cleared (no "Fjern billede").
  const canEdit = useCanEdit();
  const index = useMemo(() => resourceIndex(data.resources), [data.resources]);
  const viewSel = selectionView(type, part, index);
  const chooseView =
    canEdit && viewSel.target !== null
      ? (view: ResourceView) => {
          const w = chooseViewWrite(viewSel.target, view);
          if (w) void actions.patchResource(w.code, { [w.keyId]: w.value }, 'Ressourcebillede');
        }
      : undefined;

  const [openFrame, setOpenFrame] = useState<number | null>(null);
  const [segTarget, setSegTarget] = useState<SegmentTarget | null>(null);
  useEffect(() => {
    setOpenFrame(null);
    setSegTarget(null);
  }, [partCode]);
  // Del op opened directly starts on the most central frame, seeded at the part.
  const target: SegmentTarget | null =
    segTarget ?? (frames ? { frameId: frames[0].frame_id, seed: { u: frames[0].u, v: frames[0].v } } : null);

  const segmentFrom = (f: VisibleFrame) => {
    setSegTarget({ frameId: f.frame_id, seed: { u: f.u, v: f.v } });
    onKind('seg');
  };

  const openModel = (panoramaId: number) => {
    onClose();
    navigate(viewportPanoramaHref(panoramaId));
  };

  const created = (result: SegmentResourceResult) => {
    actions.notify(`${result.resource_code} oprettet · ${result.point_count.toLocaleString('da-DK')} punkter`);
    actions.refresh();
    onClose();
  };

  const close = (
    <IconButton size="sm" label="Luk evidens" onClick={onClose}>
      <XIcon />
    </IconButton>
  );

  if (!type || !part) {
    return (
      <div className={styles.tile}>
        <header className={styles.head}>
          <span className={styles.ident}>{partCode}</span>
          {close}
        </header>
        <div className={styles.empty}>
          {data.loaded ? <p>Delen {partCode} findes ikke (længere) i kortlægningen.</p> : <Spinner label="Indlæser delen…" />}
        </div>
      </div>
    );
  }

  const room = part.room_name.trim() || null;
  const panelId = `${baseId}-panel`;
  const tabId = (k: EvidenceKind) => `${baseId}-tab-${k}`;

  let body: ReactNode;
  if (kind === 'foto') {
    body = (
      <FotoGrid
        frames={frames}
        message={frameMessage}
        open={openFrame}
        onOpen={setOpenFrame}
        onSegment={segmentFrom}
        view={viewSel.shown}
        hasTarget={viewSel.target !== null}
        onChooseView={chooseView}
      />
    );
  } else if (kind === 'pano') {
    body = linked ? (
      <PanoView
        cloud={highlight.cloud}
        instanceId={highlight.instance_id}
        roomName={room}
        onOpenModel={openModel}
        view={viewSel.targetView}
        onChooseView={chooseView}
        onCreated={onClose}
      />
    ) : (
      <p className={styles.empty}>{PANO_TEXT.unlinked}</p>
    );
  } else {
    body = target ? (
      <SegmentView
        key={partCode}
        target={target}
        frames={frames ?? []}
        onTarget={setSegTarget}
        types={data.types}
        onCreated={created}
      />
    ) : (
      <p className={styles.empty}>{frameMessage}</p>
    );
  }

  return (
    <div className={styles.tile}>
      <header className={styles.head}>
        <span className={styles.ident}>
          <b className={styles.code}>{part.code}</b> · {type.name}
        </span>
        {room &&
          (onFocusRoom && part.room_id !== null ? (
            <button
              type="button"
              className={styles.room}
              onClick={() => part.room_id !== null && onFocusRoom(part.room_id)}
              title={`Fokusér på ${room}`}
            >
              {room}
            </button>
          ) : (
            <span className={styles.muted}>{room}</span>
          ))}
        <SegmentedControl
          role="tablist"
          aria-label="Evidens"
          className={styles.tabs}
          items={TABS.map((t) => ({ value: t.kind, label: t.label, id: tabId(t.kind), controls: panelId }))}
          value={kind}
          onChange={onKind}
        />
        {close}
      </header>
      <div id={panelId} className={styles.panel} role="tabpanel" aria-labelledby={tabId(kind)}>
        {body}
      </div>
    </div>
  );
}
